import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SIM_CHANGE_KINDS } from '@app/shared';
import { ReleasedFeature } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { ChangeAddressForm } from './ChangeAddressForm';
import { CustomerInfoForm, TagsForm } from './CustomerInfoForm';
import { useCustomerContext } from './CustomerLayout';
import { DeviceChangeForm, NumberChangeForm } from './LineChangePages';
import { EsimChangeForm, FeaturesPanel, SimBlock } from './MoreActionForms';
import { ModifyPortSection } from './ModifyPortSection';
import { Section } from './parts';

/**
 * Modify (PC401): five tabs, as on the reference (product inventory, appendix 13.5):
 * Change Address, Edit Device Info, Features and Services, Customer Information and Tag.
 * "Edit Device Info" is not a form of its own: it holds the SIM block (whose first field
 * switches it between ESN/SIM Change, Change Carrier and Change UICCID), Change Mobile
 * number, Change Device ID and E-Sim Change. A tab shows with its permission, and a block
 * inside it with its own.
 */
export function ModifyPage() {
  const { customer } = useCustomerContext();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();

  // `key` gives each customer its own form state.
  const deviceBlocks: Array<{ code: string; node: ReactNode }> = [
    {
      code: SIM_CHANGE_KINDS.find((k) => can(k.code))?.code ?? '',
      node: <SimBlock key={`sim-${customer.id}`} customer={customer} />,
    },
    {
      code: 'PC90',
      node: (
        <Section key="number" title="Change Mobile number (PC90)">
          <NumberChangeForm key={customer.id} customer={customer} />
        </Section>
      ),
    },
    {
      code: 'PC101',
      node: (
        <Section key="device" title="Change Device ID (PC101)">
          <DeviceChangeForm key={customer.id} customer={customer} />
        </Section>
      ),
    },
    {
      code: 'PC1078',
      node: (
        <Section key="esim" title="E-Sim Change (PC1078)">
          <EsimChangeForm key={customer.id} customer={customer} />
        </Section>
      ),
    },
  ].filter((b) => b.code && can(b.code));

  const tabs = [
    {
      key: 'address',
      code: 'PC100',
      title: 'Change Address',
      body: (
        <Section title="Change Address (PC100)">
          <ChangeAddressForm key={customer.id} customer={customer} />
        </Section>
      ),
    },
    {
      key: 'device',
      code: 'PC346',
      title: 'Edit Device Info',
      body: deviceBlocks.length ? (
        deviceBlocks.map((b) => b.node)
      ) : (
        <Alert severity="info">
          None of the changes of this tab (PC18, PC530, PC646, PC90, PC101, PC1078) is in your user
          group.
        </Alert>
      ),
    },
    {
      key: 'features',
      code: 'PC92',
      title: 'Features and Services',
      body: <FeaturesPanel key={customer.id} customer={customer} />,
    },
    {
      key: 'info',
      code: 'PC377',
      title: 'Customer Information',
      body: (
        <Section title="Customer Information (PC377)">
          <CustomerInfoForm key={customer.id} customer={customer} />
        </Section>
      ),
    },
    {
      key: 'tag',
      code: 'PC759',
      title: 'Tag',
      body: (
        <Section title="Tag (PC759)">
          <TagsForm customer={customer} />
        </Section>
      ),
    },
  ].filter((t) => can(t.code));

  const current = tabs.find((t) => t.key === params.get('tab')) ?? tabs[0];

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1.5 }}>
        Modify
      </Typography>
      <ReleasedFeature name="modify-port-actions">
        <ModifyPortSection customer={customer} />
      </ReleasedFeature>
      {!current ? (
        <Alert severity="info">
          None of the Modify tabs (PC100, PC346, PC92, PC377, PC759) is in your user group.
        </Alert>
      ) : (
        <>
          <Tabs
            value={current.key}
            onChange={(_e, key: string) => setParams({ tab: key }, { replace: true })}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Modify"
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            {tabs.map((t) => (
              <Tab key={t.key} value={t.key} label={`${t.title} (${t.code})`} />
            ))}
          </Tabs>
          <Box role="tabpanel" aria-label={`${current.title} (${current.code})`}>
            {current.body}
          </Box>
        </>
      )}
    </Box>
  );
}
